// src/utils/earningsLanes.js
// Creator dashboard earnings lanes (evergreen 3.7).
//
// One pure function turns a getMyCreatorMetrics payload into what the
// "Your Earnings" card renders: a summary strip (this month's pending and
// payable across the direct lanes plus the month's monthly payment when one
// was made), the Super Crit, Rumble and Memberships lanes, and the monthly
// payment lane. Every visibility rule the card had before 3.7 is reproduced
// here unchanged; the split and pool fields are additive. Evergreen 3.5b adds
// the Team memberships lane (support.teamMembershipDirect), summed into the
// strip like the other direct lanes.
//
// Server compatibility, per lane:
//  - An older server sends no pendingCents / availableCents on rumbleDirect
//    or membershipDirect: that lane reports split false and the card shows
//    its pre-3.7 line. A lane that has money without the split also hides
//    the summary strip (summary.hasSplit false), so the strip never shows a
//    total that misses a lane.
//  - An older server sends no support.pool: the pool lane stays hidden, the
//    run hint keeps its estimate form and the summary strip is hidden (its
//    payable total would miss the month's monthly payment).
//  - An older server sends no support.teamMembershipDirect: the Team
//    memberships lane stays hidden, reports zeros and the strip is exactly
//    what it was before 3.5b.
//
// Every cents field in the result is a finite number (never undefined), so
// the screen can pass them straight to its formatters.
//
// Zero imports: this file is unit-tested in plain node
// (web-landing/scripts/test-earnings-lanes.mjs).

const num = (v) => Number(v) || 0;
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

// "September 2026" from "2026-09". A malformed key falls back to a neutral
// phrase so the subtitle still reads.
export function monthLabelOf(monthKey) {
  const mk = /^(\d{4})-(\d{2})$/.exec(String(monthKey || ''));
  if (!mk) return 'this month';
  return new Date(Date.UTC(Number(mk[1]), Number(mk[2]) - 1, 1))
    .toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// data: the getMyCreatorMetrics payload (or null before it loads).
// options: { critsEnabled, membershipsEverLaunched, teamMembershipsEverLaunched,
// monthKey }. critsEnabled is accepted for the screen's call shape; no lane
// reads it (the Crit cells stay in the support card).
// teamMembershipsEverLaunched (3.5b) shows the Team memberships lane once team
// memberships have ever launched (useMembershipsLive().teamEverLaunched).
export function deriveEarningsLanes(data, options) {
  const opts = options || {};
  const membershipsEverLaunched = !!opts.membershipsEverLaunched;
  const teamMembershipsEverLaunched = !!opts.teamMembershipsEverLaunched;
  const support = (data && data.support) || {};
  const poolRaw = support.pool && typeof support.pool === 'object' ? support.pool : {};
  // The 3.7 server always sends the pool object (zeros included); an older
  // server never does.
  const poolKnown = !!support.pool && typeof support.pool === 'object';

  // Super Crits: two lanes (evergreen 3.2). RUN lane = charges recorded
  // before settings/creatorComp.directTipsSince, itemized and paid by the
  // monthly comp run; superCritCents / superCritCount / superCritEarn* are
  // run-lane only on a 3.2+ server and the whole month on an older one.
  // DIRECT lane = charges after it, credited to the creator's earnings when
  // they succeed and payable after the settlement period.
  const superCritDirect = support.superCritDirect || {};
  const grossCents = num(support.superCritCents) + num(support.superCritDirectGrossCents);
  const count = num(support.superCritCount) + num(support.superCritDirectCount);
  // Direct block: shown once the cutover is live, or whenever this month
  // already has direct credits (moving the cutover later never hides money
  // a creator was already credited).
  const showDirect = !!support.directTipsActive || num(superCritDirect.count) > 0;
  // Run-lane hint, pre-3.7 rule: run-lane numbers only.
  const runHintBefore37 =
    (support.superCritCount || 0) > 0 ||
    (support.superCritEarnCents || 0) > 0 ||
    (support.superCritEarnTestCents || 0) > 0;
  // The footnote's "paid in a later monthly payment" clause stays while the
  // run lane is the only lane (older server, or before the cutover) and in a
  // month that still has run-lane Super Crits.
  const showRunFootnote = runHintBefore37 || !showDirect;
  const pctLabel = Math.round((support.superCritCompPercent || 0.7) * 100);

  // The month's monthly payment (one creator_comp compLedger row per paid
  // month, split by its itemized breakdown on the server).
  const runs = num(poolRaw.runs);
  const paidCents = num(poolRaw.paidCents);
  const generalCents = num(poolRaw.generalCents);
  const floodsCents = num(poolRaw.floodsCents);
  const superCritRunCents = num(poolRaw.superCritRunCents);
  const poolPaidCents = isNum(poolRaw.poolPaidCents)
    ? poolRaw.poolPaidCents
    : generalCents + floodsCents;
  const paidAtMs = isNum(poolRaw.paidAtMs) && poolRaw.paidAtMs > 0 ? poolRaw.paidAtMs : null;

  // Once the run has paid this creator a Super Crit share for the month, the
  // hint shows that figure. A run that paid the month and paid no Super Crit
  // share hides the hint: "paid in a later monthly payment" would be false.
  const runPaidCents = superCritRunCents;
  const runPaid = runs > 0 && runPaidCents > 0;
  const hintMode = runPaid ? 'paid' : 'estimate';
  const hintAmountCents = runPaid ? runPaidCents : num(support.superCritEarnCents);
  const showRunHint = runHintBefore37 && !(runs > 0 && runPaidCents === 0);

  const superCrits = {
    count,
    grossCents,
    pendingCents: num(superCritDirect.pendingCents),
    availableCents: num(superCritDirect.availableCents),
    showDirect,
    showRunHint,
    hintMode,
    hintAmountCents,
    runTestCents: num(support.superCritEarnTestCents),
    runPaidCents,
    pctLabel,
    showRunFootnote,
  };

  // Rumbles (evergreen 3.3): this month's live credits from the earnings
  // ledger. Shown only once the month has one.
  const rumbleDirect = support.rumbleDirect || {};
  const rumbleCount = num(rumbleDirect.count);
  const rumbleCredited = num(rumbleDirect.creditedCents);
  const rumbleSplit = isNum(rumbleDirect.pendingCents);
  const rumbleHasMoney = rumbleCount > 0 || rumbleCredited > 0;
  const rumbles = {
    show: rumbleHasMoney,
    hasMoney: rumbleHasMoney,
    split: rumbleSplit,
    count: rumbleCount,
    creditedCents: rumbleCredited,
    pendingCents: rumbleSplit ? num(rumbleDirect.pendingCents) : 0,
    availableCents: rumbleSplit ? num(rumbleDirect.availableCents) : 0,
  };

  // Channel memberships (evergreen 3.5a): this month's live credits plus the
  // channel's current member count. While memberships have never launched
  // the lane stays hidden unless this creator already has membership
  // activity (a pilot), so the dark screen matches the pre-3.5a one.
  const membershipDirect = support.membershipDirect && typeof support.membershipDirect === 'object'
    ? support.membershipDirect
    : null;
  const memberCount = membershipDirect ? num(membershipDirect.count) : 0;
  const memberActive = membershipDirect ? num(membershipDirect.activeMembers) : 0;
  const memberCredited = membershipDirect ? num(membershipDirect.creditedCents) : 0;
  const memberSplit = !!membershipDirect && isNum(membershipDirect.pendingCents);
  const memberHasMoney = memberCount > 0 || memberCredited > 0;
  const memberships = {
    show: !!membershipDirect && (
      membershipsEverLaunched || memberCount > 0 || memberActive > 0 || memberCredited > 0
    ),
    hasMoney: memberHasMoney,
    split: memberSplit,
    count: memberCount,
    creditedCents: memberCredited,
    pendingCents: memberSplit ? num(membershipDirect.pendingCents) : 0,
    availableCents: memberSplit ? num(membershipDirect.availableCents) : 0,
    activeMembers: memberActive,
  };

  // Team memberships (evergreen 3.5b): this month's live credits of team
  // membership shares. A share is credited after the month it was paid in
  // closes, so this lane shows LAST month's team memberships (two months
  // back for a month the team had no active member). Hidden until team
  // memberships have launched unless this creator already has a credit.
  const teamDirect = support.teamMembershipDirect && typeof support.teamMembershipDirect === 'object'
    ? support.teamMembershipDirect
    : null;
  const teamCount = teamDirect ? num(teamDirect.count) : 0;
  const teamCredited = teamDirect ? num(teamDirect.creditedCents) : 0;
  const teamSplit = !!teamDirect && isNum(teamDirect.pendingCents);
  const teamHasMoney = teamCount > 0 || teamCredited > 0;
  const teamMemberships = {
    show: !!teamDirect && (teamMembershipsEverLaunched || teamHasMoney),
    hasMoney: teamHasMoney,
    split: teamSplit,
    count: teamCount,
    creditedCents: teamCredited,
    pendingCents: teamSplit ? num(teamDirect.pendingCents) : 0,
    availableCents: teamSplit ? num(teamDirect.availableCents) : 0,
  };

  // The pool lane keys on pool money only (general + floods). A run row that
  // carried only a Super Crit share feeds the Super Crit hint above.
  const pool = {
    mode: poolPaidCents > 0 ? 'paid' : 'hidden',
    paidCents,
    poolPaidCents,
    superCritRunCents,
    floodsCents,
    paidAtMs,
    monthLabel: monthLabelOf(opts.monthKey),
  };

  // The strip: the direct lanes' pending, and their available plus the whole
  // run credit for the month (credited to the balance for that month,
  // whatever its breakdown). Hidden when the payload has no pool object or
  // when any lane with money lacks the split.
  const summary = {
    pendingCents: superCrits.pendingCents + rumbles.pendingCents + memberships.pendingCents
      + teamMemberships.pendingCents,
    availableCents: superCrits.availableCents + rumbles.availableCents + memberships.availableCents
      + teamMemberships.availableCents
      + (runs > 0 ? paidCents : 0),
    hasSplit: poolKnown && !(rumbles.hasMoney && !rumbles.split) && !(memberships.hasMoney && !memberships.split)
      && !(teamMemberships.hasMoney && !teamMemberships.split),
  };

  return { summary, superCrits, rumbles, memberships, teamMemberships, pool };
}
